import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { MapPin, Phone, Facebook, Menu, X, Info, Star, Award, Layers, Building } from "lucide-react";
import ConceptCTAPill from "@/components/ConceptCTAPill";
import ConstructionInquiryForm from "@/components/ConstructionInquiryForm";

/*
  G. Uymatiao Jr. Construction — concept site. Established 1982; legacy engineering
  + building contractor with its own construction-materials manufacturing line.
  Imagery is limited, so the design leans on restrained typography, institutional
  green/cream, and real sourced credentials rather than a large photo portfolio.
*/

const GREEN = "#1D5B3A";
const GREEN_DARK = "#123D27";
const CREAM = "#F6F4EE";
const INK = "#1A1A1A";

const FB = "https://www.facebook.com/glujr/";
const NAME = "G. Uymatiao Jr. Construction";
const SLUG = "g-uymatiao-jr-construction-concept";
const P = "/construction/uymatiao";

const fadeUp = {
  initial: { opacity: 0, y: 26 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-80px" },
  transition: { duration: 0.6, ease: "easeOut" },
};

export default function UymatiaoConcept() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    const s = () => setScrolled(window.scrollY > 8);
    s(); window.addEventListener("scroll", s);
    return () => window.removeEventListener("scroll", s);
  }, []);

  const links = [["Company", "#company"], ["Capabilities", "#capabilities"], ["Materials & Supplies", "#materials"], ["Recognition", "#recognition"], ["Contact", "#contact"]];

  return (
    <div id="top" className="min-h-screen" style={{ background: CREAM, color: INK, fontFamily: "'Source Serif 4', serif" }} data-testid="uymatiao-concept-root">
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Source+Serif+4:opsz,wght@8..60,400;8..60,500;8..60,600;8..60,700&family=Inter:wght@400;500;600&display=swap');
        .glu-sans { font-family: 'Inter', sans-serif; }
        #top h1, #top h2, #top h3 { font-family: 'Source Serif 4', serif; }
      `}</style>

      <div data-testid="construction-disclaimer" className="bg-[#0F281A] text-white text-center text-xs py-2 relative z-50">
        <span className="inline-flex items-center gap-2 px-4">
          <Info size={13} className="shrink-0 text-white/60" />
          Concept website created by <a href="https://mysitecare.eu/ph" target="_blank" rel="noopener noreferrer" className="underline decoration-white/40 hover:decoration-white">MySiteCare</a> — not currently operated by or affiliated with {NAME}.
        </span>
      </div>

      <header className={`sticky top-0 z-40 bg-white transition-shadow ${scrolled ? "shadow-[0_4px_20px_rgba(0,0,0,0.08)]" : ""}`} style={{ borderBottom: `1px solid #DCD5C4` }}>
        <nav className="max-w-7xl mx-auto px-5 sm:px-8 h-20 flex items-center justify-between">
          <a href="#top" data-testid="glu-logo" className="flex items-center gap-3">
            <img src={`${P}/logo.png`} alt={NAME} className="h-10 w-10 object-contain" />
            <span className="glu-sans text-base font-semibold tracking-tight" style={{ color: GREEN_DARK }}>G. UYMATIAO JR. CONSTRUCTION</span>
          </a>
          <div className="hidden lg:flex items-center gap-8">
            {links.map(([l, h]) => (
              <a key={h} href={h} className="text-sm font-medium glu-sans" style={{ color: `${INK}99` }}>{l}</a>
            ))}
          </div>
          <a href="#contact" data-testid="glu-header-cta" className="hidden sm:inline-flex glu-sans rounded-sm px-5 py-2.5 text-sm font-semibold text-white" style={{ background: GREEN }}>
            Request a Quotation
          </a>
          <button className="lg:hidden" onClick={() => setOpen(!open)} data-testid="glu-menu-toggle" aria-label="Menu">
            {open ? <X size={24} /> : <Menu size={24} />}
          </button>
        </nav>
        {open && (
          <div className="lg:hidden px-5 pb-4 space-y-1 bg-white glu-sans">
            {links.map(([l, h]) => <a key={h} href={h} onClick={() => setOpen(false)} className="block py-2.5 font-medium" style={{ color: INK }}>{l}</a>)}
            <a href="#contact" onClick={() => setOpen(false)} data-testid="glu-mobile-cta" className="block mt-2 text-center rounded-sm px-5 py-3 text-sm font-semibold text-white" style={{ background: GREEN }}>Request a Quotation</a>
          </div>
        )}
      </header>

      {/* HERO — typography-led, restrained (limited imagery) */}
      <section className="relative py-12 md:py-16" style={{ background: GREEN_DARK }} data-testid="glu-hero">
        <div className="max-w-6xl mx-auto px-5 sm:px-8 grid lg:grid-cols-[1.05fr_.95fr] items-stretch">
          <motion.div {...fadeUp} className="py-12 md:py-16 lg:pr-14">
            <span className="glu-sans text-xs font-semibold uppercase tracking-[0.25em]" style={{ color: "#9FD2B4" }}>Dumaguete City, Negros Oriental &middot; Since 1982</span>
            <h1 className="mt-6 text-4xl sm:text-5xl lg:text-6xl leading-[1.1] text-white font-semibold">General Engineering &amp; Building Construction, Since 1982.</h1>
            <p className="mt-6 text-lg text-white/80 leading-relaxed max-w-2xl glu-sans">
              Design-and-build, project management and construction materials manufacturing for commercial, institutional and non-residential clients across Negros Oriental.
            </p>
            <div className="mt-9 flex flex-wrap items-center gap-4">
              <a href="#contact" data-testid="glu-hero-cta" className="glu-sans rounded-sm px-7 py-3.5 text-sm font-semibold text-white" style={{ background: GREEN }}>Request a Quotation</a>
              <a href="#capabilities" data-testid="glu-hero-capabilities" className="glu-sans rounded-sm border border-white/40 px-7 py-3.5 text-sm font-semibold text-white hover:bg-white/10 transition-colors">Our Capabilities</a>
            </div>
            <p className="mt-8 text-sm text-white/60 glu-sans" data-testid="glu-trust-signal">
              <Star size={14} className="inline -mt-0.5" style={{ color: "#9FD2B4" }} /> 5.0 Google Rating · 6 Reviews · 43 years in operation
            </p>
          </motion.div>
          <motion.figure {...fadeUp} className="relative min-h-[360px] lg:min-h-full overflow-hidden">
            <img src={`${P}/project-lobby.jpg`} alt="A completed commercial interior by G. Uymatiao Jr. Construction" className="absolute inset-0 w-full h-full object-cover" />
            <figcaption className="absolute right-0 bottom-0 left-0 p-4 text-xs text-white bg-black/65">Commercial interior project</figcaption>
          </motion.figure>
        </div>
      </section>

      {/* COMPANY */}
      <section id="company" className="max-w-5xl mx-auto px-5 sm:px-8 py-20 md:py-28">
        <motion.div {...fadeUp}>
          <span className="glu-sans text-xs font-semibold uppercase tracking-[0.25em]" style={{ color: GREEN }}>Our Company</span>
          <h2 className="mt-3 text-3xl sm:text-4xl font-semibold leading-snug">More Than Four Decades in Negros Oriental</h2>
          <p className="mt-6 text-lg leading-relaxed" style={{ color: `${INK}CC` }}>
            Founded in 1982 and led by Engr. Gregorio Lim Uymatiao Jr., G. Uymatiao Jr. Construction has grown alongside Dumaguete's institutional and commercial development — combining architectural and engineering services, project management, and non-residential construction with our own construction materials and equipment supply.
          </p>
        </motion.div>
      </section>

      {/* CAPABILITIES */}
      <section id="capabilities" className="py-20 md:py-28" style={{ background: "#EFEADB" }}>
        <div className="max-w-6xl mx-auto px-5 sm:px-8">
          <motion.div {...fadeUp} className="max-w-xl mb-14">
            <span className="glu-sans text-xs font-semibold uppercase tracking-[0.25em]" style={{ color: GREEN }}>What We Do</span>
            <h2 className="mt-3 text-3xl sm:text-4xl font-semibold leading-snug">Capabilities</h2>
          </motion.div>
          <div className="grid sm:grid-cols-3 gap-10">
            {[
              [Building, "Design & Build", "Architectural and engineering services combined with project management for non-residential, commercial and institutional work."],
              [Layers, "Materials Manufacturing", "We manufacture and supply our own construction materials, used on our own projects and available to other builders."],
              [Award, "Project Management", "Site oversight and project management for institutional and commercial clients across Negros Oriental."],
            ].map(([Icon, t, b]) => (
              <motion.div key={t} {...fadeUp}>
                <Icon size={26} style={{ color: GREEN }} />
                <h3 className="mt-4 text-lg font-semibold">{t}</h3>
                <p className="mt-3 text-sm leading-relaxed glu-sans" style={{ color: `${INK}90` }}>{b}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* MATERIALS & SUPPLIES */}
      <section id="materials" className="max-w-6xl mx-auto px-5 sm:px-8 py-20 md:py-28 grid md:grid-cols-2 gap-14 items-center">
        <motion.div {...fadeUp} className="order-2 md:order-1">
          <span className="glu-sans text-xs font-semibold uppercase tracking-[0.25em]" style={{ color: GREEN }}>Materials &amp; Supplies</span>
          <h2 className="mt-3 text-3xl sm:text-4xl font-semibold leading-snug">We Manufacture What We Build With</h2>
          <p className="mt-5 text-lg leading-relaxed glu-sans" style={{ color: `${INK}CC` }}>
            Our materials division produces paver blocks, modular CHB, G.I. purlins and steel deck — giving our own project timelines more control, and giving other builders a local supply source.
          </p>
          <ul className="mt-6 space-y-2 glu-sans text-sm" style={{ color: `${INK}90` }}>
            <li>&middot; Paver Blocks (Glupav)</li>
            <li>&middot; Modular CHB (Glublock)</li>
            <li>&middot; G.I. Purlins (Glupurl)</li>
            <li>&middot; Steel Deck (Gludeck)</li>
          </ul>
        </motion.div>
        <motion.div {...fadeUp} className="order-1 md:order-2 rounded-sm overflow-hidden" style={{ boxShadow: "0 10px 30px rgba(0,0,0,0.08)" }}>
          <img src={`${P}/products-catalog.jpg`} alt="Construction materials manufactured by G. Uymatiao Jr. Construction" className="w-full object-cover" loading="lazy" />
        </motion.div>
      </section>

      {/* RECOGNITION */}
      <section id="recognition" className="py-16 md:py-20" style={{ background: GREEN }}>
        <div className="max-w-4xl mx-auto px-5 sm:px-8 text-center">
          <motion.div {...fadeUp}>
            <Award size={28} className="mx-auto text-white/80" />
            <p className="mt-5 text-lg text-white leading-relaxed glu-sans">
              Recognized by the Philippine Constructors Association as a training partner/host for a Construction Safety Training Program in Negros Oriental.
            </p>
          </motion.div>
        </div>
      </section>

      {/* PROJECT INQUIRY */}
      <section id="contact" className="py-20 md:py-28" style={{ background: CREAM }}>
        <div className="max-w-6xl mx-auto px-5 sm:px-8 grid md:grid-cols-2 gap-14">
          <motion.div {...fadeUp}>
            <span className="glu-sans text-xs font-semibold uppercase tracking-[0.25em]" style={{ color: GREEN }}>Get In Touch</span>
            <h2 className="mt-3 text-3xl font-semibold leading-snug">Request a Project Quotation</h2>
            <ul className="mt-8 space-y-6 glu-sans">
              <li className="flex items-start gap-4">
                <MapPin size={20} style={{ color: GREEN }} className="mt-0.5 shrink-0" />
                <div><div className="text-xs uppercase tracking-wide" style={{ color: `${INK}60` }}>Address</div><div>49 Sta. Catalina St., Dumaguete City, Negros Oriental</div></div>
              </li>
              <li className="flex items-start gap-4">
                <Phone size={20} style={{ color: GREEN }} className="mt-0.5 shrink-0" />
                <div><div className="text-xs uppercase tracking-wide" style={{ color: `${INK}60` }}>Phone</div><a href="tel:+63352254717" className="hover:underline">(035) 225-4717</a></div>
              </li>
              <li className="flex items-start gap-4">
                <Facebook size={20} style={{ color: GREEN }} className="mt-0.5 shrink-0" />
                <div><div className="text-xs uppercase tracking-wide" style={{ color: `${INK}60` }}>Facebook</div><a href={FB} target="_blank" rel="noopener noreferrer" data-testid="glu-facebook-link" className="hover:underline">facebook.com/glujr</a></div>
              </li>
            </ul>
          </motion.div>
          <motion.div {...fadeUp}>
            <ConstructionInquiryForm
              testidPrefix="glu"
              accent={GREEN}
              accentText="#FFFFFF"
              heading="Request a Project Quotation"
              subheading="Institutional, commercial or a materials order — let us know what you need and we'll follow up."
              projectTypes={["Institutional / Commercial Build", "Project Management", "Materials Supply Order", "Other"]}
              phone="+63352254717"
              phoneDisplay="(035) 225-4717"
              messengerUrl={FB}
            />
          </motion.div>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="py-14 glu-sans" style={{ background: GREEN_DARK, color: "#FFFFFF99" }}>
        <div className="max-w-7xl mx-auto px-5 sm:px-8 grid sm:grid-cols-3 gap-10">
          <div>
            <div className="flex items-center gap-3">
              <img src={`${P}/logo.png`} alt={NAME} className="h-10 w-10 object-contain" style={{ background: "white", borderRadius: 4, padding: 4 }} />
              <span className="text-white text-base font-semibold">G. UYMATIAO JR. CONSTRUCTION</span>
            </div>
            <p className="mt-4 text-sm leading-relaxed">General engineering &amp; building construction in Dumaguete City since 1982.</p>
          </div>
          <div>
            <div className="text-white text-sm font-semibold mb-3">Capabilities</div>
            <ul className="text-sm space-y-2">
              <li>Design &amp; Build</li>
              <li>Project Management</li>
              <li>Construction Materials</li>
            </ul>
          </div>
          <div>
            <div className="text-white text-sm font-semibold mb-3">Contact</div>
            <p className="text-sm">49 Sta. Catalina St.,<br />Dumaguete City, Negros Oriental</p>
            <p className="text-sm mt-2">(035) 225-4717</p>
            <div className="flex gap-4 mt-4">
              <a href={FB} target="_blank" rel="noopener noreferrer" aria-label="Facebook"><Facebook size={18} className="hover:text-white transition-colors" /></a>
            </div>
          </div>
        </div>
        <div className="max-w-7xl mx-auto px-5 sm:px-8 mt-10 pt-6 border-t border-white/10 text-xs">
          <p>This is an unsolicited concept website created by MySiteCare. It is not operated by or affiliated with {NAME}. Project details, licenses and availability should be confirmed directly with the company.</p>
        </div>
      </footer>

      <a href={FB} target="_blank" rel="noopener noreferrer" data-testid="construction-sticky-contact"
        className="lg:hidden fixed bottom-4 inset-x-4 z-50 rounded-sm px-6 py-3.5 text-center text-sm font-semibold text-white shadow-xl glu-sans" style={{ background: GREEN }}>
        Message {NAME}
      </a>
      <ConceptCTAPill slug={SLUG} />
    </div>
  );
}
